import { useState } from "react";
import {
  CopyButton,
  PillGroup,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  type ColligativeStrings,
  CUSTOM,
  colligativeMessages,
  DEFAULT_SIG,
  SolventFields,
  TEMPERATURE_OPTIONS,
  VantHoffFields,
} from "@/components/tools/chem-physical/colligative-shared";
import { FailureNotice } from "@/components/tools/chem-physical/heat-shared";
import { WORKING_BOX } from "@/components/tools/chemistry/formula-view";
import { QuantityInput } from "@/components/tools/chemistry/quantity-input";
import { useToolNumber } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  type MolarMassSolution,
  type Property,
  solveMolarMass,
} from "@/lib/tool-kit/chem-physical/colligative";
import {
  HEAT_MASS_UNITS,
  unitFactor,
  unitOptionsOf,
  unitSymbol,
} from "@/lib/tool-kit/chem-physical/heat";
import {
  fewestSigFigs,
  formatPlain,
  formatSig,
} from "@/lib/tool-kit/chemistry/format";

const MASS_OPTIONS = unitOptionsOf(HEAT_MASS_UNITS);

function MolarMassAnswer({
  solution,
  texts,
  unit,
  sig,
  strings,
}: {
  solution: MolarMassSolution;
  texts: Record<string, string>;
  unit: { t: string; soluteMass: string; solventMass: string };
  sig: number;
  strings: ColligativeStrings;
}) {
  const fmt = useToolNumber();
  const p = (value: number) => formatPlain(fmt, value);
  const boil = solution.property === "boil";
  const answerText = `${formatSig(fmt, solution.molarMass, sig)} g/mol`;
  const lines: string[] = [];
  for (const key of ["soluteMass", "solventMass"] as const) {
    if (unitFactor(HEAT_MASS_UNITS, unit[key]) !== 1) {
      lines.push(
        t(strings.convertLine, {
          label:
            key === "soluteMass" ? strings.soluteMass : strings.solventMass,
          value: texts[key].trim(),
          unit: unitSymbol(HEAT_MASS_UNITS, unit[key]),
          converted: `${p(key === "soluteMass" ? solution.soluteGrams : solution.solventGrams)} g`,
        })
      );
    }
  }
  return (
    <>
      <StatGrid>
        <StatCard emphasis label={strings.molarMassResult} value={answerText} />
        <StatCard
          label={boil ? strings.elevation : strings.depression}
          value={`${formatSig(fmt, solution.delta, sig)} °C`}
        />
        <StatCard
          label={strings.molality}
          value={`${formatSig(fmt, solution.molality, sig)} m`}
        />
      </StatGrid>
      <div className={WORKING_BOX}>
        <p className="font-medium font-sans text-foreground">
          {strings.working}
        </p>
        <p>{strings.molarEquation}</p>
        {lines.map((line) => (
          <p key={line}>{line}</p>
        ))}
        <p>
          {t(boil ? strings.molarDeltaBoil : strings.molarDeltaFreeze, {
            observed: p(solution.observedC),
            pure: p(solution.pureC),
            delta: formatSig(fmt, solution.delta, sig + 1),
          })}
        </p>
        <p>
          {t(strings.molarMolality, {
            delta: formatSig(fmt, solution.delta, sig + 1),
            i: p(solution.i),
            k: p(solution.k),
            molality: formatSig(fmt, solution.molality, sig + 1),
          })}
        </p>
        <p>
          {t(strings.molarMoles, {
            molality: formatSig(fmt, solution.molality, sig + 1),
            kg: p(solution.solventGrams / 1000),
            moles: formatSig(fmt, solution.moles, sig + 1),
          })}
        </p>
        <p>
          {t(strings.molarResult, {
            mass: p(solution.soluteGrams),
            moles: formatSig(fmt, solution.moles, sig + 1),
            result: `${formatSig(fmt, solution.molarMass, sig + 1)} g/mol`,
          })}
        </p>
      </div>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        text={t(strings.copyText, {
          label: strings.molarMassResult,
          value: answerText,
        })}
      />
    </>
  );
}

export function MolarMassPanel({
  strings,
  sigChoice,
}: {
  strings: ColligativeStrings;
  sigChoice: string;
}) {
  const [property, setProperty] = useState<Property>("freeze");
  const [solvent, setSolvent] = useState("benzene");
  const [choice, setChoice] = useState("sucrose:ideal");
  const [text, setText] = useState({
    k: "",
    point: "",
    observed: "2.32",
    soluteMass: "4.00",
    solventMass: "55.0",
    i: "1",
  });
  const [unit, setUnit] = useState({
    t: "C",
    soluteMass: "g",
    solventMass: "g",
  });
  const outcome = solveMolarMass({ property, solvent, text, unit });
  const set = (patch: Partial<typeof text>) => setText({ ...text, ...patch });
  const sig =
    sigChoice === "match"
      ? fewestSigFigs(
          [text.observed, text.soluteMass, text.solventMass],
          DEFAULT_SIG
        )
      : Number(sigChoice);
  const labels: Record<string, string> = {
    k: t(strings.customK, {
      constant: property === "boil" ? strings.kb : strings.kf,
    }),
    point:
      property === "boil"
        ? strings.pureBoilingPoint
        : strings.pureFreezingPoint,
    observed:
      property === "boil"
        ? strings.observedBoilingPoint
        : strings.observedFreezingPoint,
    soluteMass: strings.soluteMass,
    solventMass: strings.solventMass,
    i: strings.vantHoff,
  };
  return (
    <>
      <PillGroup
        label={strings.propertyLabel}
        name="col-mm-property"
        onChange={setProperty}
        options={[
          { value: "freeze", label: strings.propertyFreeze },
          { value: "boil", label: strings.propertyBoil },
        ]}
        value={property}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <SolventFields
          idPrefix="col-mm"
          k={text.k}
          onK={(k) => set({ k })}
          onPoint={(point) => set({ point })}
          onSolvent={setSolvent}
          onTempUnit={(t2) => setUnit({ ...unit, t: t2 })}
          point={text.point}
          property={property}
          solvent={solvent}
          strings={strings}
          tempUnit={unit.t}
        />
        <QuantityInput
          id="col-mm-observed"
          label={labels.observed}
          onUnitChange={(value) => setUnit({ ...unit, t: value })}
          onValueChange={(observed) => set({ observed })}
          unit={unit.t}
          unitLabel={strings.unitTemperature}
          units={TEMPERATURE_OPTIONS}
          value={text.observed}
        />
        <QuantityInput
          id="col-mm-solute"
          label={strings.soluteMass}
          onUnitChange={(value) => setUnit({ ...unit, soluteMass: value })}
          onValueChange={(soluteMass) => set({ soluteMass })}
          unit={unit.soluteMass}
          unitLabel={strings.unitMass}
          units={MASS_OPTIONS}
          value={text.soluteMass}
        />
        <QuantityInput
          id="col-mm-solvent"
          label={strings.solventMass}
          onUnitChange={(value) => setUnit({ ...unit, solventMass: value })}
          onValueChange={(solventMass) => set({ solventMass })}
          unit={unit.solventMass}
          unitLabel={strings.unitMass}
          units={MASS_OPTIONS}
          value={text.solventMass}
        />
        <VantHoffFields
          choice={choice}
          idPrefix="col-mm"
          onChoice={(next, factor) => {
            setChoice(next);
            if (factor !== null) {
              set({ i: factor });
            }
          }}
          onValue={(i) => {
            setChoice(CUSTOM);
            set({ i });
          }}
          strings={strings}
          value={text.i}
        />
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {outcome.ok ? (
          <MolarMassAnswer
            sig={sig}
            solution={outcome}
            strings={strings}
            texts={text}
            unit={unit}
          />
        ) : (
          <FailureNotice
            failure={outcome}
            labels={labels}
            messages={colligativeMessages(strings)}
          />
        )}
      </section>
    </>
  );
}
